import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { isNavGroup, isNavMega, type LinkTarget, type NavEntry } from './types'
import { SmartLink } from './SmartLink'
import { useIsMobileNav } from '../hooks/useMediaQuery'

export interface HeaderNavProps {
  entries: NavEntry[]
}

/**
 * The primary navigation. It lives in the header above 992px and inside the off-canvas
 * sidebar below it — the caller renders it into whichever container is correct for the
 * current width, never both. See `AppShell`.
 *
 * Sub-menus are deliberately asymmetric: the `.active` toggle on `.has-sub-menu` is a
 * **mobile-only** click handler, while on desktop the sub-menu is revealed by CSS
 * hover alone.
 *
 * The current page carries `.current` — and because the header and the mobile sidebar
 * render this same element, the marker follows into both. Matching is by whole path, not
 * by string prefix: `/stay` must not light up while you are on `/stay/rooms`, and `/`
 * must only match itself or every entry would be current.
 *
 * An entry with an `href` is another KKF app on its own domain. It is **never** current,
 * whatever the path happens to be: `garage.kkfmongolia.com/services` and this app's
 * `/services` are different pages that share a path.
 *
 * A `NavMega` entry opens a panel of product cards instead of a list of link names, and
 * it follows the sub-menu's asymmetry exactly: click to open below 992px, CSS hover above
 * it. See `.ul-header-mega` in `_header.scss`.
 *
 * **A cross-app entry opens in a new tab** (`newTab`). A visitor who clicks Resort in the
 * header is exploring the group, not leaving the page they were reading, and the page they
 * were reading should still be there when they come back. In-app entries never do this —
 * a second tab of the same app is nobody's intent. The rule lives here rather than in
 * `SmartLink`'s default, so a footer link or a card is unaffected.
 */
export function HeaderNav({ entries }: HeaderNavProps) {
  const isMobile = useIsMobileNav()
  const [openLabel, setOpenLabel] = useState<string | null>(null)
  const { pathname } = useLocation()

  const isCurrent = ({ to, href }: LinkTarget) => {
    if (href !== undefined || to === undefined) return false
    return to === '/' ? pathname === '/' : pathname === to
  }

  return (
    <div className="to-go-to-sidebar-in-mobile">
      <nav className="ul-header-nav">
        {entries.map((entry) =>
          isNavMega(entry) ? (
            <div
              key={entry.label}
              className={[
                'has-sub-menu has-mega',
                isMobile && openLabel === entry.label ? 'active' : '',
                entry.items.some(isCurrent) || isCurrent(entry.cta) ? 'current' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              {...(isMobile
                ? { onClick: () => setOpenLabel((cur) => (cur === entry.label ? null : entry.label)) }
                : {})}
            >
              <a role="button">{entry.label}</a>

              <div className="ul-header-mega">
                <div className="ul-header-mega-items">
                  {entry.items.map((item) => (
                    <SmartLink
                      key={item.title}
                      to={item.to}
                      href={item.href}
                      newTab={item.href !== undefined}
                      className="ul-header-mega-card"
                    >
                      <span className="ul-header-mega-card-media">
                        <img src={item.image} alt={item.imageAlt} />
                      </span>
                      <span className="ul-header-mega-card-body">
                        {item.meta && <span className="ul-header-mega-card-meta">{item.meta}</span>}
                        <span className="ul-header-mega-card-title">{item.title}</span>
                        <span className="ul-header-mega-card-txt">{item.descr}</span>
                      </span>
                    </SmartLink>
                  ))}
                </div>

                <SmartLink
                  to={entry.cta.to}
                  href={entry.cta.href}
                  newTab={entry.cta.href !== undefined}
                  className="ul-header-mega-cta"
                >
                  {entry.cta.label} <i className="flaticon-fast-forward-double-right-arrows-symbol" />
                </SmartLink>
              </div>
            </div>
          ) : isNavGroup(entry) ? (
            <div
              key={entry.label}
              className={[
                'has-sub-menu',
                isMobile && openLabel === entry.label ? 'active' : '',
                entry.children.some(isCurrent) ? 'current' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              {...(isMobile
                ? { onClick: () => setOpenLabel((cur) => (cur === entry.label ? null : entry.label)) }
                : {})}
            >
              <a role="button">{entry.label}</a>

              <div className="ul-header-submenu">
                <ul>
                  {entry.children.map((child) => (
                    <li key={child.label}>
                      <SmartLink
                        to={child.to}
                        href={child.href}
                        newTab={child.href !== undefined}
                        className={isCurrent(child) ? 'current' : undefined}
                      >
                        {child.label}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <SmartLink
              key={entry.label}
              to={entry.to}
              href={entry.href}
              newTab={entry.href !== undefined}
              className={isCurrent(entry) ? 'current' : undefined}
              aria-current={isCurrent(entry) ? 'page' : undefined}
            >
              {entry.label}
            </SmartLink>
          ),
        )}
      </nav>
    </div>
  )
}
